// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { readStoredUser } from '../utils/userStorage';

/**
 * @typedef {Object} User
 * @property {string} id - ID do utilizador
 * @property {string} name - Nome
 * @property {string} email - Email
 * @property {string} username - Username
 */

/**
 * AuthContext - Contexto global de autenticação
 * @type {React.Context}
 */
const AuthContext = createContext();

/**
 * Hook para aceder ao contexto de autenticação
 * @returns {Object} Objeto com user, setUser, userTravels, setUserTravels, isLoadingAuth
 */
export const useAuth = () => useContext(AuthContext);

const readStoredTravels = () => {
    if (typeof window === 'undefined') return [];
    try {
        const storedTravels = localStorage.getItem('user-travels');
        return storedTravels ? JSON.parse(storedTravels) : [];
    } catch (_) {
        return [];
    }
};

/**
 * AuthProvider - Componente provider para autenticação
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes filhos
 * @returns {React.ReactElement}
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(readStoredUser);
    const [userTravels, setUserTravels] = useState(readStoredTravels);
    // Stored auth is restored synchronously. The app-wide launch animation is
    // an overlay in AppWrapper, so it never has to masquerade as auth loading
    // or hold route rendering/API requests behind a timer.
    const isLoadingAuth = false;

    // Round 57 — listen for the axios-helper "auth:logout" event so we
    // can wipe the in-memory user when the JWT filter rejects a banned
    // account mid-session. The event carries `reason: 'banned'` plus
    // the server message; we stash that in sessionStorage so the Login
    // page can surface it as a toast on next mount.
    useEffect(() => {
        const onAuthLogout = (e) => {
            const detail = e?.detail || {};
            if (detail.isAdmin) return; // admin uses its own context
            setUser(null);
            setUserTravels([]);
            if (detail.reason === 'banned' && detail.message) {
                try {
                    sessionStorage.setItem('gm:banned-message', detail.message);
                } catch {}
            }
        };
        window.addEventListener('auth:logout', onAuthLogout);
        return () => window.removeEventListener('auth:logout', onAuthLogout);
    }, []);

    // Memoize o valor do context para evitar re-renders desnecessários
    const value = useMemo(() => ({
        user,
        setUser,
        userTravels,
        setUserTravels,
        isLoadingAuth
    }), [user, userTravels, isLoadingAuth]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
